/**
 * Enviar's file boundary: a common interface to OPEN (send) and
 * SAVE (receive) that resolves differently per platform —
 *
 *   - Web (browser / PWA): the browser's native download (`<a download>`)
 *     or, if the flow explicitly asks for it, the File System Access API. To
 *     open, the standard `<input type="file">` (which already invokes the OS picker).
 *   - Native (Tauri, desktop/Android): the OS "Open…" / "Save as…" dialogs
 *     + direct FS access (`@tauri-apps/plugin-dialog` + `-fs`),
 *     better UX (the user chooses folder and name, it writes to disk without going
 *     through the WebView's downloads folder).
 *
 * Tauri imports are DYNAMIC and guarded by `isNativePlatform()`: in the
 * web build they never execute (they stay in a lazy chunk that is never loaded),
 * so the browser bundle does not drag the native runtime.
 */

/**
 * Are we running inside Tauri's WebView? Tauri injects `__TAURI_INTERNALS__` into
 * `window` before the app loads. In the browser/PWA it does not exist.
 */
/** Minimal handle of the File System Access API (only what we use). */
interface SaveFileHandle {
  createWritable: () => Promise<{
    write: (data: Blob) => Promise<void>;
    close: () => Promise<void>;
  }>;
}

declare global {
  // File System Access API — it is not in the default DOM types; it only exists
  // in desktop Chromium. It is declared optional and checked at runtime.
  var showSaveFilePicker:
    | ((opts: { suggestedName?: string }) => Promise<SaveFileHandle>)
    | undefined;
}

export function isNativePlatform(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** File name from an OS path (separators `/` or `\`). */
function baseName(path: string): string {
  const parts = path.split(/[/\\]/);
  return parts.at(-1) || "archivo";
}

/**
 * Saves a `Blob` to disk choosing the best mechanism of the platform.
 * Native: OS "Save as…" dialog. Web: File System Access API if
 * available (desktop Chromium), otherwise, download via `<a download>`.
 */
async function saveBlob(blob: Blob, suggestedName: string): Promise<void> {
  if (isNativePlatform()) {
    await saveNative(blob, suggestedName);
    return;
  }
  await saveWeb(blob, suggestedName);
}

/** Same as `saveBlob` but starting from a `blob:` URL (the one we already have in receive). */
export async function saveBlobUrl(url: string, suggestedName: string): Promise<void> {
  const blob = await (await fetch(url)).blob();
  await saveBlob(blob, suggestedName);
}

async function saveNative(blob: Blob, suggestedName: string): Promise<void> {
  const [{ save }, { writeFile }] = await Promise.all([
    import("@tauri-apps/plugin-dialog"),
    import("@tauri-apps/plugin-fs"),
  ]);
  const path = await save({ defaultPath: suggestedName });
  if (!path) {
    return; // user cancelled
  }
  const bytes = new Uint8Array(await blob.arrayBuffer());
  await writeFile(path, bytes);
}

/**
 * File System Access API for the desktop (Chromium): a true
 * "Save as…". The user's dismissal (`AbortError`) is NOT a failure. If the
 * API does not exist (Firefox/Safari/iOS PWA) it falls back to the classic download.
 */
async function saveWeb(blob: Blob, suggestedName: string): Promise<void> {
  const picker = globalThis.showSaveFilePicker;
  if (typeof picker === "function") {
    try {
      const handle = await picker({ suggestedName });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    } catch (err) {
      // Cancelling the dialog throws AbortError — it is not a real error.
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      // Any other failure: falls back to the classic download.
    }
  }
  downloadViaAnchor(blob, suggestedName);
}

function downloadViaAnchor(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Opens the OS file picker and returns `File[]` to send them. On web
 * it is NOT used (the `<input type="file">` already invokes the picker); it is the native path:
 * Tauri's "Open…" dialog + direct FS read. A synthetic `File` wraps
 * the bytes so the rest of the flow (`blobSource`) does not distinguish the origin.
 */
export async function openFilesNative(): Promise<File[]> {
  const [{ open }, { readFile }] = await Promise.all([
    import("@tauri-apps/plugin-dialog"),
    import("@tauri-apps/plugin-fs"),
  ]);
  const selection = await open({ multiple: true });
  if (!selection) {
    return [];
  }
  const paths = Array.isArray(selection) ? selection : [selection];
  return Promise.all(
    paths.map(async (path) => {
      const bytes = await readFile(path);
      // `File([Uint8Array])` — clones the bytes into the blob; it is not re-read from disk
      // in windows, but sends via dialog are occasional and this keeps
      // an interface identical to the browser's `<input>`.
      return new File([new Uint8Array(bytes)], baseName(path));
    }),
  );
}
